import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { PrivacySettings } from '../components/PrivacySettings';
import { 
  UserCircleIcon, 
  CogIcon, 
  ShieldCheckIcon, 
  ChartBarIcon,
  ClockIcon,
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface UserStats {
  totalPrompts: number;
  promptsThisWeek: number;
  avgImprovementLength: number;
  mostUsedCategory: string;
  timeSaved: number;
}

interface RecentPrompt {
  id: string;
  original_prompt: string;
  improved_prompt: string;
  created_at: string;
  was_used: boolean;
  improvement_type?: string;
}

type TabType = 'overview' | 'settings' | 'privacy' | 'history';

export const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [recentPrompts, setRecentPrompts] = useState<RecentPrompt[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/get-started');
      return;
    }
    loadDashboardData();
  }, [user, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load user settings
      const settingsRes = await apiClient.get('/api/auth/settings');
      setSettings(settingsRes.data);

      // Load analytics if allowed
      try {
        const analyticsRes = await apiClient.get('/api/auth/privacy/analytics');
        if (!analyticsRes.data.error) {
          setStats({
            totalPrompts: analyticsRes.data.total_prompts || 0,
            promptsThisWeek: analyticsRes.data.prompts_this_week || 0,
            avgImprovementLength: analyticsRes.data.avg_improvement_length || 0,
            mostUsedCategory: analyticsRes.data.most_used_category || 'general',
            timeSaved: analyticsRes.data.total_time_saved || 0
          });
        }
      } catch (err) {
        console.log('Analytics not available');
      }

      // Load recent prompts if available
      try {
        const historyRes = await apiClient.get('/api/auth/history?limit=5');
        setRecentPrompts(historyRes.data || []);
      } catch (err) {
        console.log('History not available');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <SparklesIcon className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <SparklesIcon className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-xl font-semibold">Promptasaurus Dashboard</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <CogIcon className="h-5 w-5" />
                Settings
              </div>
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'privacy'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5" />
                Privacy
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5" />
                History
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Prompts</p>
                      <p className="text-2xl font-semibold text-gray-900">{stats.totalPrompts}</p>
                    </div>
                    <SparklesIcon className="h-12 w-12 text-blue-100" />
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">This Week</p>
                      <p className="text-2xl font-semibold text-gray-900">{stats.promptsThisWeek}</p>
                    </div>
                    <ChartBarIcon className="h-12 w-12 text-green-100" />
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Time Saved</p>
                      <p className="text-2xl font-semibold text-gray-900">{Math.round(stats.timeSaved / 60)}m</p>
                    </div>
                    <ClockIcon className="h-12 w-12 text-purple-100" />
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Top Category</p>
                      <p className="text-2xl font-semibold text-gray-900 capitalize">{stats.mostUsedCategory}</p>
                    </div>
                    <CheckCircleIcon className="h-12 w-12 text-orange-100" />
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
              {recentPrompts.length > 0 ? (
                <div className="space-y-4">
                  {recentPrompts.map((prompt) => (
                    <div key={prompt.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 line-clamp-1">{prompt.original_prompt}</p>
                          <p className="text-sm font-medium text-gray-900 mt-1 line-clamp-2">
                            {prompt.improved_prompt}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 ml-4">
                          {new Date(prompt.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No recent activity. Start improving prompts to see them here!</p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center justify-between p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <SparklesIcon className="h-6 w-6 text-blue-600" />
                    <span className="font-medium">Try the Improver</span>
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                </button>
                
                <button
                  onClick={() => setActiveTab('settings')}
                  className="flex items-center justify-between p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <CogIcon className="h-6 w-6 text-gray-600" />
                    <span className="font-medium">Configure Settings</span>
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold mb-6">Extension Settings</h2>
            {settings && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Endpoint
                  </label>
                  <input
                    type="text"
                    value={settings.api_endpoint}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Configure this in the browser extension settings
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User Category
                  </label>
                  <select
                    value={settings.user_category || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  >
                    <option value="">General</option>
                    <option value="business">Business Professional</option>
                    <option value="academic">Academic/Research</option>
                    <option value="creative">Creative/Marketing</option>
                    <option value="technical">Technical/Engineering</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Show Preview</h3>
                    <p className="text-sm text-gray-500">Preview improvements before applying</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    settings.show_preview 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {settings.show_preview ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Auto Improve</h3>
                    <p className="text-sm text-gray-500">Automatically improve prompts</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    settings.auto_improve 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {settings.auto_improve ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    Sync Version: {settings.sync_version} • 
                    Last Synced: {settings.last_synced_at 
                      ? new Date(settings.last_synced_at).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'privacy' && (
          <PrivacySettings />
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold mb-6">Prompt History</h2>
            {recentPrompts.length > 0 ? (
              <div className="space-y-4">
                {recentPrompts.map((prompt) => (
                  <div key={prompt.id} className="border rounded-lg p-4 hover:border-blue-500 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm text-gray-500">
                        {new Date(prompt.created_at).toLocaleString()}
                      </span>
                      {prompt.improvement_type && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {prompt.improvement_type}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Original</p>
                        <p className="text-sm text-gray-700">{prompt.original_prompt}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Improved</p>
                        <p className="text-sm text-gray-900 font-medium">{prompt.improved_prompt}</p>
                      </div>
                    </div>
                    {prompt.was_used && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                        <CheckCircleIcon className="h-4 w-4" />
                        Used
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No prompt history available</p>
                <p className="text-sm text-gray-400 mt-2">
                  Enable prompt storage in privacy settings to see your history
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};